<template lang="pug">
v-layout(class='workspace')
  v-flex(class='lft')
    tools(
      :buttons='buttons'
      :activeStates='activeButtons'
      :inactiveStates='inactiveButtons'
      @click='buttonHandler'
    )
  v-flex(class='main')
    div(class='art' ref='art')
    div(class='grd' ref='grd')
  view-box-modal(
    :opened='viewBoxModalOpened'
    :view-box='viewBox'
    @undo='closeViewBoxModal'
    @ok='updateViewBox'
  )
  v-flex(class='rgt d-flex')
    div(class='extension d-flex')
      div(class='extension-top')
        v-toolbar(color='white' flat)
          div(style='line-height: 1.2')
            div(class='title') Помещения
            div(class='body-1 grey--text') на этаже
      div(class='extension-body')
        slot(name='extension')
</template>

<script>
import * as d3 from 'd3'
// import polylabel from 'polylabel'
import { debounce, chunk } from 'lodash'
import colors from 'vuetify/es5/util/colors'
import { mapMutations, mapState, mapGetters } from 'vuex'

import Tools from './Tools'
import ViewBoxModal from './ViewBoxModal'

function getBoundingBoxCenter(selection) {
  try {
    let element = selection.node()
    let bbox = element.getBBox()
    return [bbox.x + bbox.width / 2, bbox.y + bbox.height / 2]
  } catch (e) {
    return [0, 0]
  }
}

export default {
  components: {
    Tools,
    ViewBoxModal
  },

  props: {
    source: null,
    placements: {
      type: Array,
      default: () => []
    },
    itemId: {
      type: String,
      default: 'uuid'
    },
    itemPoints: {
      type: String,
      default: 'points'
    },
    fileViewBox: {
      type: String
    }
  },

  data: () => ({
    dragging: false,
    startPoint: null,
    drawing: false,
    canDraw: false,
    imageOk: false,
    points: [],
    selected: null,
    scale: 1,
  }),

  destroyed() {
    if (this.art) {
      this.art.remove()
      delete this.art
    }

    if (this.grd) {
      this.grd.remove()
      delete this.grd
    }

    this.unlisten()
  },

  created() {
    // кнопка альт нажата
    this.altPressed = false

    // Установим viewbox с сервера
    if (this.fileViewBox) {
      const [x, y, w, h] = this.fileViewBox.split(' ')
      this.updateViewBox({ x, y, w, h })
    }
  },

  mounted() {
    this.drawLineGrid()
    this.drawArtboard()
    this.initDrawMode()

    if (this.source && this.source.name) {
      this.uploadImage(this.source)
    } else {
      this.insertImage(this.source)
    }

    this.createPolygons()
    this.unlisten = this.listen()
  },

  methods: {
    keydownHandler(event) {
      this.altPressed = event.key === 'Alt'
    },

    createPolygons() {
      // TODO: вынести это отсюда как можно дальше
      this.placements.forEach(model => {
        // Создадим полигоны
        if (null !== model[this.itemPoints]) {
          this.createPolygon({
            points: model[this.itemPoints],
            model
          })
        }
      })
    },

    onKeydown({ key }) {
      this.altPressed = key === 'Alt'
    },

    onKeyup({ key }) {
      this.altPressed = false

      if ('Escape' === key && this.drawing) {
        if (null !== this.g) {
          this.g.remove()
          this.g = null
        }

        this.drawing = false
        this.canDraw = false
        this.points = []
      }
    },

    loadImage(url) {
      return new Promise(resolve => {
        let xhr = new XMLHttpRequest()
        let error = () => {
          this.showSnack('Ошибка загрузки картинки')
        }

        xhr.onload = () => {
          let reader = new FileReader()

          reader.onloadend = () => {
            if (xhr.response.type.match(/image.*/)) {
              resolve(reader.result)
            } else {
              error()
            }
          }

          reader.readAsDataURL(xhr.response)
        }

        xhr.onerror = error

        xhr.open('GET', url)
        xhr.responseType = 'blob'
        xhr.send()
      })
    },

    listen() {
      window.addEventListener('keyup', this.onKeyup)
      window.addEventListener('keydown', this.onKeydown)
      window.addEventListener('resize', this.drawLineGrid)
      return () => {
        window.removeEventListener('keyup', this.onKeyup)
        window.removeEventListener('resize', this.drawLineGrid)
        window.removeEventListener('keydown', this.onKeydown)
      }
    },

    clickOutside() {
      let { selected } = this
      if (null !== selected) {
        d3.select(selected).attr('stroke', 'none')
        this.selected = null
      }
    },

    enableZoom() {
      if (void 0 === this.zoomHandler) {
        this.zoomHandler = this.zoom()
      }

      this.art.call(this.zoomHandler)
    },

    disableZoom() {
      this.art.on('.zoom', null)
    },

    drawArtboard() {
      this.art = d3
        .select(this.$refs.art)
        .append('svg')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('viewBox', this.computedViewBox)

      this.container = this.art.append('g')
      this.enableZoom()
    },

    insertImage(image) {
      this.container.select('.image').remove()

      this.container
        .insert('svg:image', ':first-child')
        .attr('class', 'image')
        .attr('is-image', true)
        .attr('xlink:href', image)
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', '100%')
        .attr('height', '100%')
        .on('click', this.clickOutside)

      this.imageOk = true
    },

    drawLineGrid: debounce(function() {
      let { innerWidth, innerHeight } = window

      if (void 0 === this.grd) {
        this.grd = d3
          .select(this.$refs.grd)
          .append('svg')
          .attr('width', '100%')
          .attr('height', '100%')
      }

      this.grd
        .append('g')
        .attr('class', 'x axis')
        .selectAll('line')
        .data(d3.range(0, innerWidth, 10))
        .enter()
        .append('line')
        .attr('x1', d => d)
        .attr('y1', 0)
        .attr('x2', d => d)
        .attr('y2', innerHeight)

      this.grd
        .append('g')
        .attr('class', 'y axis')
        .selectAll('line')
        .data(d3.range(0, innerHeight, 10))
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('y1', d => d)
        .attr('x2', innerWidth)
        .attr('y2', d => d)
    }, 500),

    initDrawMode() {
      let vm = this
      let svg = this.container

      this.dragger = d3.behavior
        .drag()
        .on('drag', handleDrag)
        .on('dragend', () => {
          vm.dragging = false
          vm.saveData()
        })

      function handleDrag() {
        if (vm.drawing) return

        let circle
        let newPoints = []
        let dragCircle = d3.select(this)

        vm.dragging = true

        let poly = d3.select(this.parentNode).select('polygon')
        let circles = d3.select(this.parentNode).selectAll('circle.circle')

        dragCircle.attr('cx', d3.event.x).attr('cy', d3.event.y)

        for (let i = 0; i < circles[0].length; i++) {
          circle = d3.select(circles[0][i])
          newPoints.push([circle.attr('cx'), circle.attr('cy')])
        }

        poly.attr('points', newPoints)
      }

      svg.on('mousedown', function() {
        if (d3.event.target.hasAttribute('is-handle')) {
          vm.disableZoom()
        }
      })

      svg.on('mouseup', function() {
        let tgt = d3.select(d3.event.target)

        if (tgt.attr('is-handle')) {
          vm.enableZoom()
        }

        if (tgt.attr('is-first-handle')) {
          vm.$emit('create', {
            points: vm.points
          })
          return
        }

        if (!vm.canDraw || vm.dragging || tgt.attr('is-handle')) {
          return
        }

        vm.drawing = true
        vm.startPoint = [d3.mouse(this)[0], d3.mouse(this)[1]]

        if (svg.select('g.drawPoly').empty()) {
          vm.g = svg
            .append('g')
            .attr('class', 'drawPoly')
            .attr('pointer-events', 'none')
        }

        vm.points.push(d3.mouse(this))

        vm.g.select('polyline').remove()
        vm.g.selectAll('circle').remove()

        vm.g
          .append('polyline')
          .attr('points', vm.points)
          .attr('stroke', colors.red.base)
          .style('fill', 'none')

        for (let i = 0; i < vm.points.length; i++) {
          vm.g
            .append('circle')
            .data([vm.points[i]])
            .attr('cx', vm.points[i][0])
            .attr('cy', vm.points[i][1])
            .attr('r', 2)
            .attr('fill', colors.red.base)
            .attr('is-handle', 'true')
            .attr('class', 'circle')
            .attr('is-first-handle', () => {
              return 0 === i ? true : null
            })
            .style('cursor', 'pointer')
            .style('pointer-events', 'auto')
          // .attr('cx', d => d[0] * vm.scale)
          // .attr('cy', d => d[1] * vm.scale)
          // .attr('transform', () => `scale(${1 / vm.scale})`)
        }
      })

      svg.on('mousemove', function() {
        if (!vm.drawing) return

        let g = d3.select('g.drawPoly')

        g.select('line').remove()

        g.append('line')
          .attr('x1', vm.startPoint[0])
          .attr('y1', vm.startPoint[1])
          .attr('x2', d3.mouse(this)[0])
          .attr('y2', d3.mouse(this)[1])
          .attr('stroke', colors.red.base)
          .attr('stroke-width', 1)
      })
    },

    createPolygon({ points, model }) {
      let vm = this
      let svg = this.container

      let g = svg.append('g')
      g.attr('class', 'polygon')
      g.data([model])

      svg.select('g.drawPoly').remove()

      g.append('polygon')
        .attr('points', points)
        .style('fill', colors.green.base)
        .attr('fill-opacity', 0.3)
        .attr('is-polygon', true)
        .on('click', function() {
          if (!vm.canDraw) {
            vm.selected = this.parentNode
          }
        })

      for (let i = 0; i < points.length; i++) {
        const nextPoint = points[i + 1] ? points[i + 1] : points[0]
        g.append('line')
          .attr('x1', points[i][0])
          .attr('y1', points[i][1])
          .attr('x2', nextPoint[0])
          .attr('y2', nextPoint[1])
          .attr('stroke', colors.green.base)
          .attr('stroke-width', 2)
          .on('mouseenter', function () {
            if (vm.canDraw && !vm.selected) {
              const $this = d3.select(this)
              // $this.attr('stroke', colors.green.base)
              $this.attr('stroke-width', 4)
            }
          })
          .on('mouseleave', function () {
            if (vm.canDraw && !vm.selected) {
              const $this = d3.select(this)
              // $this.attr('stroke', colors.green.base)
              $this.attr('stroke-width', 2)
            }
          })
          .on('click', function () {
            if (vm.altPressed) {
              const $this = d3.select(this)
              const stIndex = points.findIndex(([x, y]) => x == $this.attr('x1') && y == $this.attr('y1'))
              const npoints = [...points]
              npoints.splice(stIndex + 1, 0, d3.mouse(this))
              g.remove()
              vm.createPolygon({ points: npoints, model })
            }
          })
        //
        g.append('circle')
          .data([points[i]])
          .attr('cx', points[i][0])
          .attr('cy', points[i][1])
          .attr('r', 3)
          .attr('fill', colors.red.base)
          .attr('is-handle', 'true')
          .attr('class', 'circle')
          .style({ cursor: 'move' })
          .call(vm.dragger)
          .on('mousedown', function () {
            if (vm.altPressed) {
              let npoints = points.filter(([x, y]) => x !== points[i][0] && y !== points[i][1])
              g.remove()
              vm.createPolygon({ points: npoints, model })
              setTimeout(() => {
                vm.initDrawMode()
                vm.enableZoom()
              })
            }
          })
      }

      let [pcx, pcy] = getBoundingBoxCenter(g)
      let placement = vm.placements.findIndex(x => {
        return x[vm.itemId] === model[vm.itemId]
      }) + 1

      g.append('circle')
        .attr('r', 13)
        .attr('fill', 'white')
        .attr('cx', pcx)
        .attr('cy', pcy)
        .attr('class', 'label')
        .attr('stroke', colors.green.base)

      g.append('text')
        .attr('font-weight', 'bold')
        .attr('font-size', '12px')
        .attr('opacity', 0.6)
        .text(placement)
        .attr('text-anchor', 'middle')
        .attr('x', pcx)
        .attr('y', pcy + 4)

      vm.drawing = false
      vm.canDraw = true
      vm.points = []

      vm.updatePointsHandles()
    },

    uploadImage(file) {
      // TODO: было ранее здесь по событию
      // let file = event.target.files[0]
      let imageType = /image.*/

      if (file.type.match(imageType)) {
        let reader = new FileReader()

        reader.onload = () => {
          this.insertImage(reader.result)
        }

        reader.readAsDataURL(file)
      } else {
        this.showSnack('Неподдерживаемый формат файла')
      }
    },

    updateViewBox(viewBox) {
      this.setViewBox(viewBox)

      if (this.art) {
        this.art.attr('viewBox', this.computedViewBox)
      }

      this.closeViewBoxModal()
    },

    toggleDrawing() {
      this.canDraw = !this.canDraw
    },

    saveData() {
      let results = this.getDrawResults()
      this.$emit('update', results)
    },

    getDrawResults() {
      let results = []

      this.container.selectAll('polygon').each(function() {
        let target = d3.select(this)

        let points = target.attr('points')
        let [model = {}] = target.data()

        if (null !== points) {
          results.push({
            ...model,
            points: chunk(
              points
                .split(/,/)
                .map(x => +x)
                .map(x => x.toFixed(5)),
              2
            )
          })
        }
      })

      return results
    },

    buttonHandler({ name }) {
      let method

      switch (name) {
        case this.buttons.upload:
          method = 'uploadImage'
          break

        case this.buttons.remove:
          method = 'removeSelected'
          break

        case this.buttons.create:
          method = 'toggleDrawing'
          break

        case this.buttons.resize:
          method = 'openViewBoxModal'
          break

        case this.buttons.save:
          method = 'saveData'
          break
      }

      if (void 0 !== this[method]) {
        this[method].apply(this, arguments)
      }
    },

    removeSelected() {
      let node = d3.select(this.selected)
      let [model] = node.data()

      if (node.empty()) {
        return
      }

      node.remove()
      this.selected = null

      this.$emit('remove', model)
    },

    updatePointsHandles() {
      let circs = this.container.selectAll('[is-handle]')
      circs.style({ display: this.canDraw ? 'block' : 'none' })
    },

    zoom() {
      return d3.behavior
        .zoom()
        .scaleExtent([0.5, 25])
        .on('zoom', () => {
          // TODO: как-то закешировать эти коллекции
          // this.container.selectAll('circle')
          //   .attr('cx', d => d[0] * d3.event.scale)
          //   .attr('cy', d => d[1] * d3.event.scale)
          //   .attr('transform', () => `scale(${1 / d3.event.scale})`)

          this.container.attr(
            'transform',
            `translate(${d3.event.translate})
            scale(${d3.event.scale})`
          )

          this.scale = d3.event.scale
        })
    },

    ...mapMutations('app', ['showSnack']),

    ...mapMutations('editor', [
      'setViewBox',
      'openViewBoxModal',
      'closeViewBoxModal'
    ])
  },

  computed: {
    activeButtons() {
      let { create } = this.buttons
      return {
        [create]: this.canDraw
      }
    },

    inactiveButtons() {
      let { save, remove, create, resize, upload } = this.buttons
      return {
        [save]: !this.imageOk,
        [remove]: !this.selected,
        [create]: !this.imageOk,
        [resize]: !this.imageOk,
        [upload]: !!this.source
      }
    },

    ...mapState('editor', ['buttons', 'viewBox', 'viewBoxModalOpened']),

    ...mapGetters('editor', ['getRandomColor', 'computedViewBox'])
  },

  watch: {
    canDraw: 'updatePointsHandles',

    selected(next, prev) {
      if (next) {
        d3.select(next).select('polygon').attr('stroke', colors.red.base)
        d3.select(next).selectAll('line').attr('stroke', colors.red.base)
      }
      if (prev) {
        d3.select(prev).select('polygon').attr('stroke', 'none')
        d3.select(prev).selectAll('line').attr('stroke', colors.green.base)
      }
    },

    source(image) {
      this.insertImage(image)
    },

    placements() {
      this.container.selectAll('g.polygon').remove()
      this.createPolygons()
    }
  }
}
</script>

<style lang="scss">
.workspace {
  .axis line {
    fill: none;
    shape-rendering: crispEdges;
    stroke: rgba(0, 0, 0, 0.04);
    vector-effect: non-scaling-stroke;
  }
}
</style>

<style lang="scss" scoped>
.workspace {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: absolute;
  max-width: 100% !important;
  overflow: hidden;
}
.lft {
  flex: 0 1 auto;
  background: #f5f5f5;
  border-right: solid 1px rgba(0, 0, 0, 0.1);
}
.rgt {
  width: 230px;
  flex: 0 1 auto;
  position: relative;
  flex-direction: column;
}
.main {
  flex-grow: 1;
  background: white;
  position: relative;
}
.art {
  @extend .workspace;
  z-index: 2;
}
.grd {
  @extend .workspace;
  z-index: 1;
}
.extension {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: absolute;
  flex-direction: column;
  border-left: solid 1px rgba(0, 0, 0, 0.1);
}
.extension-top {
  flex: 0 !important;
  border-bottom: solid 1px rgba(0, 0, 0, 0.1);
}
.extension-body {
  position: relative;
}
</style>
